import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { graphql } from 'gatsby';
import { Inline, SocialIcon } from 'chaoskit/src/components';

import { Data } from '../components';
import BaseLayout from '../layouts/BaseLayout';
import Hero from '../partials/Hero';
import google from '../assets/media/logo-google.svg';
import rsa from '../assets/media/logo-rsa.svg';
import amazon from '../assets/media/logo-amazon.svg';
import netflix from '../assets/media/logo-netflix.svg';

const Team = props => {
  const {
    data: {
      file: { childDataYaml: pageData },
    },
  } = props;

  return (
    <BaseLayout>
      <Helmet
        title={pageData.title}
        meta={[{ name: 'description', content: pageData.description }]}
      />
      <Hero className="u-gradient--teal-darkBlue">
        <div className="hero-content">
          <div className="container">
            <div className="hero-titleWrapper">
              <h1 className="hero-title">
                <Data>{pageData.hero.heading}</Data>
              </h1>
              <div className="hero-titleSub">
                <Data markdown>{pageData.hero.blurb}</Data>
              </div>
            </div>
          </div>
        </div>
      </Hero>
      <section className="section section--large section--slant">
        <div className="container">
          <div className="section-titleWrapper">
            <h3 className="section-title">
              <Data>{pageData.section1.heading}</Data>
            </h3>
            <div className="section-titleSub">
              <Data markdown>{pageData.section1.blurb}</Data>
            </div>
          </div>
        </div>
      </section>
      {pageData.section1.items.map(founder => (
        <section
          key={founder.name}
          className="section section--large section--slant section--shadow team__founder-container u-pv--remove"
        >
          <div className="team__founder-wrapper">
            <div
              className="team__founder-image"
              style={{ backgroundImage: `url(${founder.photo})` }}
            />
            <div className="team__founder-content">
              <div className="u-textCenter u-textLeft@medium">
                <h4 className="u-mb--remove">
                  <Data>{founder.name}</Data>
                </h4>
                <h5 className="u-textMuted u-mt--xsmall">
                  <Data>{founder.title}</Data>
                </h5>
              </div>

              <div className="u-textMedium u-pv--regular">
                <Data markdown>{founder.blurb}</Data>
              </div>
              {(founder.twitter || founder.linkedin) && (
                <Inline>
                  {founder.twitter && (
                    <SocialIcon url={founder.twitter} service="twitter" />
                  )}
                  {founder.linkedin && (
                    <SocialIcon url={founder.linkedin} service="linkedin" />
                  )}
                </Inline>
              )}
            </div>
          </div>
        </section>
      ))}
      <section className="section section--large section--slant section--shadow">
        <div className="container">
          <div className="section-titleWrapper">
            <h3 className="section-title">
              <Data>{pageData.section2.heading}</Data>
            </h3>
            <div className="section-titleSub">
              <Data>{pageData.section2.blurb}</Data>
            </div>
          </div>
          <Inline className="company-logos u-flexCenter" size="xlarge">
            <img src={google} alt="Google" />
            <img src={rsa} alt="RSA" />
            <img src={amazon} className="company-logos--amazon" alt="Amazon" />
            <img src={netflix} alt="Netflix" />
          </Inline>
        </div>
      </section>
      <section className="section section--large">
        <div className="container">
          <div className="section-titleWrapper">
            <h3 className="section-title">
              <Data>{pageData.section3.heading}</Data>
            </h3>
            <div className="section-titleSub">
              <Data markdown>{pageData.section3.blurb}</Data>
            </div>
          </div>
          <div className="container">
            <ul className="blockGrid blockGrid-2@medium blockGrid-3@large blockGrid--xlargeGutter u-flexCenter">
              {pageData.section3.items.map(backer => (
                <li key={backer.name}>
                  <div className="team__investor-wrapper">
                    <img
                      src={backer.photo}
                      alt={backer.name}
                      className="team__investor-profile"
                    />
                    <div
                      className="team__investor-logo"
                      style={{ backgroundImage: `url(${backer.logo})` }}
                    />
                  </div>

                  <div className="u-textCenter">
                    <h4 className="u-mb--remove">
                      <Data>{backer.name}</Data>
                    </h4>
                    <h5 className="u-textMuted u-mt--small">
                      <Data>{backer.company}</Data>
                    </h5>
                  </div>
                  <Data markdown>{backer.blurb}</Data>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </BaseLayout>
  );
};

Team.propTypes = {
  data: PropTypes.object.isRequired,
};

export default Team;

export const pageQuery = graphql`
  query TeamPageData {
    file(name: { eq: "team" }) {
      childDataYaml {
        title
        description
        hero {
          blurb
          heading
        }
        section1 {
          blurb
          heading
          items {
            blurb
            linkedin
            name
            photo
            title
            twitter
          }
        }
        section2 {
          blurb
          heading
        }
        section3 {
          blurb
          heading
          items {
            blurb
            company
            logo
            name
            photo
          }
        }
      }
    }
  }
`;
